import React, { useState, useEffect, useRef } from 'react';
import adminConsoleService from '@services/adminConsoleService';

interface LicenseInfo {
  CustomerName: string;
  CustomerEmail: string;
  TotalUsers: number;
  LicenseType: string;
  DateExpiration: string;
  Expired: boolean;
  ErrorExpiredMessage: string | null;
  Signatured: boolean;
  ErrorSignaturedMessage: string | null;
}

function calcDaysForExpiration(dateStr: string): number
{
  const exp = new Date(dateStr);
  const now = new Date();
  const diff = exp.getTime() - now.getTime();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

function getLicenseStatus(info: LicenseInfo): string
{
  if (!info.Signatured) return info.ErrorSignaturedMessage || 'Invalid Signature';
  if (info.Expired) return info.ErrorExpiredMessage || 'Expired';
  return 'Valid';
}

// ─── Read-only field ──────────────────────────────────────────────────────────

const InfoField: React.FC<{ label: string; value: string; highlight?: boolean }> = ({
  label,
  value,
  highlight,
}) => (
  <div className="flex items-center mb-3">
    <span className="text-[12px] text-[#A5A7AC] dark:text-[#beb7ac] w-[160px] flex-shrink-0">
      {label}
    </span>
    <div className="flex-1 bg-[#f5f5f5] dark:bg-[#333535] border border-[#e0e0e0] rounded px-3 py-1.5 text-[12px] text-[#333] min-h-[30px]">
      <span className={highlight ? 'text-[#5697b3] font-medium' : ''}>{value}</span>
    </div>
  </div>
);

// ─── Main Tab ─────────────────────────────────────────────────────────────────

export const SubscriptionTab: React.FC = () =>
{
  const [license, setLicense] = useState<LicenseInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Upload state
  const [uploadMode, setUploadMode] = useState(false);
  const [licFile, setLicFile] = useState<File | null>(null);
  const [pkeyFile, setPkeyFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const licInputRef = useRef<HTMLInputElement>(null);
  const pkeyInputRef = useRef<HTMLInputElement>(null);

  const loadLicense = async () =>
  {
    try
    {
      setLoading(true);
      setError(null);
      const data = await adminConsoleService.getLicense();
      setLicense(data);
    }
    catch
    {
      setError('Failed to load license information.');
    }
    finally
    {
      setLoading(false);
    }
  };

  useEffect(() =>
  {
    loadLicense();
  }, []);

  const handleLicFileChange = (e: React.ChangeEvent<HTMLInputElement>) =>
  {
    const f = e.target.files?.[0] ?? null;
    setLicFile(f);
    if (f && !uploadMode) setUploadMode(true);
  };

  const handlePkeyFileChange = (e: React.ChangeEvent<HTMLInputElement>) =>
  {
    setPkeyFile(e.target.files?.[0] ?? null);
  };

  const handleCancel = () =>
  {
    setUploadMode(false);
    setLicFile(null);
    setPkeyFile(null);
    setUploadError(null);
    if (licInputRef.current) licInputRef.current.value = '';
    if (pkeyInputRef.current) pkeyInputRef.current.value = '';
  };

  const handleSave = async () =>
  {
    if (!licFile || !pkeyFile)
    {
      setUploadError('Please select both the license file and the public key file.');
      return;
    }
    setSaving(true);
    setUploadError(null);
    try
    {
      await adminConsoleService.uploadLicense(licFile, pkeyFile);
      handleCancel();
      await loadLicense();
    }
    catch
    {
      setUploadError('Failed to upload license. Please check the files and try again.');
    }
    finally
    {
      setSaving(false);
    }
  };

  return (
    <div className="px-6 py-5 max-w-[640px]">
      {/* Title */}
      <h3 className="text-[16px] font-normal text-[#5697b3] mb-4">Subscription</h3>

      {/* Global error */}
      {error && (
        <div className="mb-4 px-3 py-2 bg-[#fdecea] dark:bg-[#3a1a1a] border border-[#e57373] dark:border-[#8b4444] rounded text-[12px] text-[#c62828] dark:text-[#e57373] flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="material-icons text-[14px]">error_outline</span>
            {error}
          </div>
          <button onClick={() => setError(null)}>
            <span className="material-icons text-[14px]">close</span>
          </button>
        </div>
      )}

      {/* Card */}
      <div className="border border-[#ddd] rounded-sm bg-white shadow-sm">
        {/* ── Information section ── */}
        <div className="px-5 pt-4 pb-2">
          <p className="text-[13px] text-[#555] font-medium mb-3">Information:</p>

          {loading ? (
            <div className="flex items-center gap-2 py-4 text-[12px] text-[#aaa]">
              <span className="material-icons animate-spin text-[16px] text-[#62aacb]">
                refresh
              </span>
              Loading license information…
            </div>
          ) : license ? (
            <>
              <InfoField label="Name:" value={license.CustomerName} />
              <InfoField label="Email:" value={license.CustomerEmail} />
              <InfoField label="License Type:" value={license.LicenseType} />
              <InfoField
                label="Total Users:"
                value={`${license.TotalUsers.toLocaleString()} users`}
                highlight
              />
              <InfoField
                label="Days for Expiration:"
                value={`${calcDaysForExpiration(license.DateExpiration).toLocaleString()} days`}
                highlight
              />
              <InfoField label="Status:" value={getLicenseStatus(license)} />
            </>
          ) : (
            <p className="text-[12px] text-[#aaa] py-2">No license information available.</p>
          )}
        </div>

        {/* Separator */}
        <div className="border-t border-[#e8e8e8] mx-5" />

        {/* ── Upload File section ── */}
        <div className="px-5 py-4">
          <p className="text-[13px] text-[#555] font-medium mb-3">Upload File:</p>

          {/* Upload error */}
          {uploadError && (
            <div className="mb-3 px-3 py-2 bg-[#fdecea] dark:bg-[#3a1a1a] border border-[#e57373] dark:border-[#8b4444] rounded text-[12px] text-[#c62828] dark:text-[#e57373] flex items-center justify-between gap-2">
              <span>{uploadError}</span>
              <button onClick={() => setUploadError(null)}>
                <span className="material-icons text-[14px]">close</span>
              </button>
            </div>
          )}

          {/* License file picker */}
          <div className="flex items-center mb-3 gap-3">
            <span
              className={`text-[12px] w-[160px] flex-shrink-0 ${uploadMode ? 'text-[#e57373]' : 'text-[#A5A7AC] dark:text-[#beb7ac]'}`}
            >
              Choose a license file:
            </span>
            <label className="flex items-center gap-2 cursor-pointer">
              <span className="px-3 py-1 text-[12px] border border-[#d0d0d0] dark:border-[#4a4c4c] rounded bg-[#f5f5f5] hover:bg-[#eaeaea] dark:hover:bg-[#3a3c3c] transition-colors text-[#555]">
                Choose File
              </span>
              <span className="text-[12px] text-[#888]">
                {licFile ? licFile.name : 'No file chosen'}
              </span>
              <input
                ref={licInputRef}
                type="file"
                accept=".lic"
                className="hidden"
                onChange={handleLicFileChange}
              />
            </label>
          </div>

          {/* Public key file picker — shown in upload mode */}
          {uploadMode && (
            <div className="flex items-center mb-4 gap-3">
              <span className="text-[12px] text-[#e57373] w-[160px] flex-shrink-0">
                Choose a public key file:
              </span>
              <label className="flex items-center gap-2 cursor-pointer">
                <span className="px-3 py-1 text-[12px] border border-[#d0d0d0] dark:border-[#4a4c4c] rounded bg-[#f5f5f5] hover:bg-[#eaeaea] dark:hover:bg-[#3a3c3c] transition-colors text-[#555]">
                  Choose File
                </span>
                <span className="text-[12px] text-[#888]">
                  {pkeyFile ? pkeyFile.name : 'No file chosen'}
                </span>
                <input
                  ref={pkeyInputRef}
                  type="file"
                  accept=".publickey"
                  className="hidden"
                  onChange={handlePkeyFileChange}
                />
              </label>
            </div>
          )}

          {/* Cancel / Save buttons — shown in upload mode */}
          {uploadMode && (
            <div className="flex gap-2 justify-end">
              <button
                onClick={handleCancel}
                disabled={saving}
                className="px-4 py-1.5 text-[12px] bg-[#aaa] dark:bg-[#555] text-white rounded hover:bg-[#999] dark:hover:bg-[#666] transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !licFile || !pkeyFile}
                className="px-4 py-1.5 text-[12px] bg-[#62aacb] text-white rounded hover:bg-[#4e99ba] transition-colors disabled:opacity-50"
              >
                {saving ? 'Uploading…' : 'Save'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
